import React, { useState, useEffect, useCallback, useRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';
import Modal from 'react-modal';
import BarLoader from 'react-spinners/BarLoader';
import ReactTooltip from 'react-tooltip';

import { faCirclePause, faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import {
  faEllipsisV,
  faDownload,
  faUpload,
  faFileUpload,
  faLock,
} from '@fortawesome/free-solid-svg-icons';

import axios from 'axios';

/** helpers */
import { openFile } from '../../helpers/file';
import { sanitizeHtml } from '../../helpers/format';

/** service */
import { fileUpload } from '../../service/api';

const FileSingleItem = ({
  field,
  fieldValue,
  handleSetData,
  handleSetDataError,
  handleSaveFileProperty,
  currentFileItem,
  currentItemIndex,
  updateFileValue,
  isUploadingProps = false,
  isNew = false,
}) => {
  const [fileSource, setFileSource] = useState();
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [replaceFileModal, setReplaceFileModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(isUploadingProps);
  const [error, setError] = useState('');
  const [isProtected, setIsProtected] = useState(currentFileItem.isProtected);
  const [newPassword, setNewPassword] = useState('');

  /** Used to get the latest ( updated ) currentFileItem props object */
  const updatedCurrentFileItem = useRef();
  useEffect(() => {
    updatedCurrentFileItem.current = currentFileItem;
  }, [currentFileItem]);

  const onDrop = useCallback(async (acceptedFiles) => {
    setFiles(acceptedFiles);
  }, []);

  useEffect(() => {
    setIsUploading(isUploadingProps);
  }, [isUploadingProps]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
  });

  const uploadFileAction = async (file, currentFileItem, fileName, itemIdx) => {
    handleSetDataError(field, '');

    const source = axios.CancelToken.source();
    setFileSource(source);

    // We have fieldValue[activeindex]?.fileName value only for those fields that exist under feature.format, so for now check this to see if we need to use name or fileType
    updateFileValue(currentItemIndex, {
      isUploading: true,
      fileType: field.fileType,
      isCanceled: false,
      fileName: fileName,
      error: '',
    });

    try {
      const formData = new FormData();
      formData.append('file', file);
      setError('');
      handleSetDataError('');
      const res = await fileUpload(formData, field.fileType, source.token);
      if (res && res.data) {
        updateFileValue(itemIdx, {
          isUploading: false,
          id: res.data.fileId,
          fileName: fileName,
          fileType: field.fileType,
          status: 'SUBMITTED',
          showMeta: '1',
          isProtected: '0',
          isPwdShow: '0',
          password: null,
          category: field.subSection,
        });
      }
      return true;
    } catch (e) {
      if (e.message == 'Operation canceled by the user.') {
        setError('Replace canceled by the user.');
      } else {
        let errorMsg = '';
        if (e?.response?.data?.errors[0]?.message == 'Request size exceeds limit: 20971520') {
          errorMsg = 'Upload failed, please make sure the file size does not exceed 20 MB';
        } else {
          errorMsg = 'An unexpected error occurred while uploading this file.';
        }
        handleSetDataError(
          field,
          'An unexpected error occurred while uploading this file. Please try again, if the error persists please contact support.'
        );
        setError(errorMsg);
      }
      if (currentFileItem) {
        updateFileValue(itemIdx, {
          isUploading: false,
          isCanceled: false,
        });
      } else {
        // Reset to previous state
        // handleSetData(field, fieldValue);
      }
    }
    return false;
  };

  const confirmReplaceFile = useCallback(async () => {
    setReplaceFileModal(false);
    if (files.length == 0) {
      return false;
    }
    const fileName = currentFileItem.fileName;
    setIsUploading(true);
    setError('');
    const uploadRes = await uploadFileAction(files[0], currentFileItem, fileName, currentItemIndex);
    if (!uploadRes) {
      setIsUploading(false);
      return;
    }

    try {
      await handleSaveFileProperty();
    } catch (e) {
      console.log(e);
      // File Input level error
      handleSetDataError(
        field,
        'An unexpected error occurred while uploading this file. Please try again, if the error persists please contact support.'
      );
      // Item level error under File Input
      setError('An unexpected error occurred while uploading this file.');
    }
    setIsUploading(false);
  }, [files, fieldValue]);

  const handleUploadCancel = () => {
    if (isUploading) {
      if (isNew) {
        updatedCurrentFileItem.current.source.cancel('Operation canceled by the user.');
      } else {
        fileSource.cancel('Operation canceled by the user.');
      }
      updateFileValue(currentItemIndex, {
        isUploading: false,
        isCanceled: true,
      });
      setIsUploading(false);
    }
  };

  const handleSetProtected = (e) => {
    setIsProtected(e.target.value);
  };

  const handleChangePassword = async (e) => {
    setNewPassword(e.target.value);
  };

  const handleToggleShowPassword = () => {
    updateFileValue(currentItemIndex, {
      isPwdShow: currentFileItem.isPwdShow == '1' ? '0' : '1',
    });
  };

  const handleFileDownload = () => {
    if (currentFileItem && currentFileItem.id) {
      openFile(currentFileItem.id, currentFileItem?.fileType, field.fileType, true);
    } else {
      return;
    }
  };

  const confirmDeleteFile = () => {
    updateFileValue(currentItemIndex, {
      deleted: true,
    });
    setOpenConfirmModal(false);
    handleSaveFileProperty();
  };

  const confirmSavePassword = () => {
    if (isProtected == '1') {
      updateFileValue(currentItemIndex, {
        isProtected: '1',
        password: newPassword,
      });
      handleSaveFileProperty();
    }
    setOpenPasswordModal(false);
  };

  const renderFileUploadArea = () => {
    const fileInputName = currentFileItem.fileName ?? field.name;
    return (
      <div className="file-upload-area">
        <div
          {...getRootProps({
            className: `drag-drop-section dropzone`,
          })}
        >
          <div className={`drag-drop-file`}>
            <label>
              <FontAwesomeIcon icon={faFileUpload} color={'#413273'} fontSize={30} />
              <p className="drag-file">{isUploading ? 'Uploading...' : 'Replace Document'}</p>
            </label>
            <input {...getInputProps()} name={fileInputName} spellCheck={false} />
          </div>
        </div>
        {isUploading && (
          <div className="file-upload-progress-section">
            <>
              <div className="d-flex justify-content-between">
                <p className="drag-file">Uploading...</p>
                <div className="d-flex file-upload-icons">
                  <FontAwesomeIcon
                    className="cursor-pointer"
                    icon={faCirclePause}
                    fontSize={20}
                    color="#051326"
                    onClick={handleUploadCancel}
                  />
                  <FontAwesomeIcon
                    className="cursor-pointer"
                    icon={faCircleXmark}
                    fontSize={20}
                    color="#e8555a"
                    onClick={handleUploadCancel}
                  />
                </div>
              </div>
              <div>
                <label htmlFor={`input-file-${field.name}`} className="d-flex">
                  <BarLoader color="#C6B9DF" loading speedMultiplier={0.5} width={300} height={6} />
                </label>
              </div>
            </>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="file-item-row">
        <div className="file-name" onClick={handleFileDownload}>
          {currentFileItem && currentFileItem.id ? <FontAwesomeIcon icon={faDownload} /> : ''}
          {currentFileItem.fileName && (
            <p data-tip={sanitizeHtml(currentFileItem.fileName)}>
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(currentFileItem.fileName),
                }}
              ></span>
            </p>
          )}
          {(currentFileItem.password == 'masked' || currentFileItem.isProtected == '1') && (
            <FontAwesomeIcon icon={faLock} color="#EB5757" />
          )}
        </div>
        <div className={`file-upload-progress-section ${isUploading ? '' : 'hide'}`}>
          {/* <div className="d-flex justify-content-between">
            <p className="drag-file">Uploading...</p>
          </div> */}
          <div>
            <label htmlFor={`input-file-${field.name}`} className="d-flex">
              <BarLoader color="#C6B9DF" loading speedMultiplier={0.5} width={300} height={6} />
            </label>
          </div>
        </div>
        {updatedCurrentFileItem.current?.error && (
          <div className="error-div">
            <div className="error-text">{updatedCurrentFileItem.current?.error}</div>
          </div>
        )}
        {error && (
          <div className="error-div">
            <div className="error-text">{error}</div>
          </div>
        )}
        {isUploading ? (
          <FontAwesomeIcon
            className="cursor-pointer"
            icon={faCircleXmark}
            fontSize={20}
            color="#e8555a"
            onClick={handleUploadCancel}
          />
        ) : (
          currentFileItem?.id && (
            <div className="options-menu">
              <div className="menu-title">
                <p>Options</p>
                <span>
                  <FontAwesomeIcon icon={faEllipsisV} />
                </span>
              </div>
              <div className="menu-content">
                <a onClick={() => setOpenPasswordModal(true)}>Set Password</a>
                <a onClick={handleFileDownload}>View</a>
                <a onClick={() => setReplaceFileModal(true)}>Replace</a>
                <a style={{ color: '#EB5757' }} onClick={() => setOpenConfirmModal(true)}>
                  Remove
                </a>
              </div>
            </div>
          )
        )}
      </div>

      <Modal isOpen={openPasswordModal} className="set-password-modal">
        <div>
          <div className="modal-header">
            <h3 className="text-center">Set Password</h3>
            <span className="modal-close" onClick={() => setOpenPasswordModal(false)}>
              x
            </span>
          </div>
          <div className="modal-content">
            <div className="inner-content">
              <div className="file-password">
                <div>
                  <label htmlFor={`password-${field.name}`}>
                    Password protected?
                    <select
                      id={`input-file-${field.name}-isPassword-protected`}
                      value={isProtected}
                      onChange={(e) => handleSetProtected(e)}
                      spellCheck={false}
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </label>
                </div>
                {isProtected == '1' && (
                  <div className="file-set-password">
                    <div className="set-password">
                      <label htmlFor={`input-file-${field.name}-password`}>Set Password</label>
                      <div className="input-file">
                        <input
                          id={`input-file-${field.name}-password`}
                          type={currentFileItem?.isPwdShow == '1' ? 'text' : 'password'}
                          placeholder="Enter Password Here"
                          value={newPassword}
                          onChange={(e) => handleChangePassword(e)}
                          className="w-100"
                          spellCheck={false}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={confirmSavePassword} type="button">
                Save
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal isOpen={replaceFileModal} className="replace-file-modal">
        <div className="">
          <span className="modal-close" onClick={() => setReplaceFileModal(false)}>
            x
          </span>
          <div className="modal-header">
            <h3 className="text-center">Replace a file</h3>
          </div>
          <div className="modal-content">
            <div>{renderFileUploadArea()}</div>
            <div>
              <button type="button" onClick={confirmReplaceFile}>
                Replace file
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal isOpen={openConfirmModal} className="confirm-modal">
        <div>
          <span className="modal-close" onClick={() => setOpenConfirmModal(false)}>
            x
          </span>
          <div className="modal-header">
            <h3 className="text-center">Confirm</h3>
          </div>
          <div className="modal-content">
            <div>
              <p>Are you sure you want to delete this file? This action cannot be undone. </p>
            </div>
            <div className="d-flex justify-content-center">
              <button
                type="button"
                onClick={() => setOpenConfirmModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button type="button" onClick={() => confirmDeleteFile()} className="del-btn">
                Delete
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FileSingleItem;
